import { createSlice, PayloadAction } from "@reduxjs/toolkit";

/**
 * Registration document state
 * Manages uploaded documents during user registration
 */
export interface RegistrationDocState {
  register: any[]; // TODO: Define proper document type
  share: any[];
  fund: any[];
  bank: any[];
  finance: any[];
}

const initialState: RegistrationDocState = {
  register: [],
  share: [],
  fund: [],
  bank: [],
  finance: [],
};

/**
 * Generic reducer factory for document setters
 * Eliminates duplication for document array updates
 */
const createDocumentSetter = <K extends keyof RegistrationDocState>(field: K) => {
  return (state: RegistrationDocState, action: PayloadAction<any[]>) => {
    state[field] = action.payload;
  };
};

const docSlice = createSlice({
  name: "doc",
  initialState,
  reducers: {
    // Document field setters - Unwrapped payload pattern
    setRegisterDoc: createDocumentSetter("register"),
    setShareDoc: createDocumentSetter("share"),
    setFundDoc: createDocumentSetter("fund"),
    setBankDoc: createDocumentSetter("bank"),
    setFinanceDoc: createDocumentSetter("finance"),

    /**
     * Bulk update multiple fields at once
     * Useful for hydrating state from API response
     */
    hydrateRegistrationDocuments: (state, action: PayloadAction<Partial<RegistrationDocState>>) => {
      return { ...state, ...action.payload };
    },

    /**
     * Clear a specific document type
     */
    clearRegistrationDocumentType: (state, action: PayloadAction<keyof RegistrationDocState>) => {
      state[action.payload] = [];
    },

    /**
     * Reset all documents to initial state
     */
    resetAllRegistrationDocuments: () => initialState,
  },
});

export const {
  setRegisterDoc,
  setShareDoc,
  setBankDoc,
  setFinanceDoc,
  setFundDoc,
  hydrateRegistrationDocuments,
  clearRegistrationDocumentType,
  resetAllRegistrationDocuments,
} = docSlice.actions;

export default docSlice.reducer;
